import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import PlayerAvatar from "../PlayerAvatar/PlayerAvatar";
import "./VersusLaserRing.css";

export interface LaserRingPlayer {
  id: string;
  name: string;
  color: string;
}

export interface LaserTarget {
  x: number;  // viewport px — centre of the group sequence
  y: number;
  rx: number; // half-width of the sequence box
  ry: number; // half-height
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

interface Props {
  players: LaserRingPlayer[];
  active: boolean;       // avatars present (fly in); false → avatars fly off + fade
  firing: boolean;       // beams fire toward the centre (starts after the avatars have flown in)
  target: LaserTarget | null;
  isMobile?: boolean;
}

/**
 * STANDARD_VERSUS laser effect. The BEAM layer renders inline inside sc-stage (position: fixed so it
 * spans the viewport un-clipped) at a z-index BELOW the ranking bars, so the lasers converge to the
 * sequence centre and end BEHIND the bars while still showing over the panel background. The AVATAR
 * layer is portaled onto <body> above everything, ringing the screen edges. Avatars fly in at the
 * start and out as the co-op comparison settles. Purely decorative (pointer-events: none).
 */
export default function VersusLaserRing({ players, active, firing, target, isMobile }: Props) {
  const [vp, setVp] = useState({ w: typeof window !== "undefined" ? window.innerWidth : 1280, h: typeof window !== "undefined" ? window.innerHeight : 800 });
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => { window.removeEventListener("resize", onResize); cancelAnimationFrame(raf); };
  }, []);

  if (!target) return null;
  const { w, h } = vp;
  const cx = target.x, cy = target.y;
  const shown = entered && firing; // beams fire once avatars have flown in and firing begins
  // The animated turbulence/displacement filter and the constellation spin re-rasterise a
  // full-viewport SVG every frame — fine on a desktop GPU, a slideshow on a phone. Mobile gets a
  // static beam (wide translucent stroke + white core reads as the same glow) with no filters.
  const heavyFx = !isMobile && !prefersReducedMotion();

  let rx = Math.max(150, Math.min(cx, w - cx) - w * 0.09);
  let ry = Math.max(140, Math.min(cy, h - cy) - h * 0.09);
  // Desktop: a true circle (single radius). Mobile keeps the oval (tall/narrow viewport).
  if (!isMobile) { const r = Math.min(rx, ry); rx = r; ry = r; }
  const exitRx = rx + w * 0.28, exitRy = ry + h * 0.28;
  const avatarPx = isMobile ? 40 : 52;
  const N = Math.max(players.length, 1);
  const angleOf = (i: number) => (-90 + (i * 360) / N) * (Math.PI / 180);

  // Beam layer — inline inside sc-stage, fixed to the viewport, BEHIND the ranking bars.
  const beamLines = players.map((p, i) => {
    const a = angleOf(i);
    const ax = cx + rx * Math.cos(a);
    const ay = cy + ry * Math.sin(a);
    return (
      <g key={p.id} className="vlr-beam-g" style={{ opacity: shown ? 1 : 0 }}>
        {/* Mobile substitute for the drop-shadow glow: a wide, very soft stroke under the beam. */}
        {!heavyFx && (
          <line x1={ax} y1={ay} x2={cx} y2={cy} stroke={p.color} strokeWidth={14} strokeLinecap="round"
            opacity={0.18} pathLength={100} className="vlr-line"
            style={{ strokeDashoffset: shown ? 0 : 100, transitionDelay: `${i * 0.04}s` }} />
        )}
        <line x1={ax} y1={ay} x2={cx} y2={cy} stroke={p.color} strokeWidth={7} strokeLinecap="round"
          opacity={0.5} pathLength={100} className="vlr-line"
          style={{
            strokeDashoffset: shown ? 0 : 100,
            transitionDelay: `${i * 0.04}s`,
            ...(heavyFx ? { filter: `drop-shadow(0 0 6px ${p.color})` } : null),
          }} />
        <line x1={ax} y1={ay} x2={cx} y2={cy} stroke="#ffffff" strokeWidth={2.2} strokeLinecap="round"
          opacity={0.98} pathLength={100} className="vlr-line"
          style={{ strokeDashoffset: shown ? 0 : 100, transitionDelay: `${i * 0.04}s` }} />
      </g>
    );
  });

  const flare = (
    <circle cx={cx} cy={cy} r={shown ? Math.max(target.rx, target.ry) : 0} fill="url(#vlr-flare)"
      className="vlr-flare-c" style={{ opacity: shown ? 0.9 : 0 }} />
  );

  const beams = (
    <svg className="vlr-beams" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <defs>
        {heavyFx && (
          <filter id="vlr-energy" x="-30%" y="-30%" width="160%" height="160%">
            <feTurbulence type="fractalNoise" baseFrequency="0.008 0.028" numOctaves={2} seed={3} result="noise">
              <animate attributeName="baseFrequency" dur="0.7s" values="0.008 0.028;0.013 0.042;0.008 0.028" repeatCount="indefinite" />
            </feTurbulence>
            <feDisplacementMap in="SourceGraphic" in2="noise" scale={13} xChannelSelector="R" yChannelSelector="G" result="disp" />
            <feGaussianBlur in="disp" stdDeviation="2.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="disp" />
            </feMerge>
          </filter>
        )}
        <radialGradient id="vlr-flare">
          <stop offset="0%" stopColor="#fff" stopOpacity="1" />
          <stop offset="40%" stopColor="#fff" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>

      {heavyFx ? (
        /* Slowly spin the whole constellation around the sequence centre. */
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from={`0 ${cx} ${cy}`} to={`360 ${cx} ${cy}`} dur="34s" repeatCount="indefinite" />
          <g filter="url(#vlr-energy)">{beamLines}</g>
          {flare}
        </g>
      ) : (
        <g>
          {beamLines}
          {flare}
        </g>
      )}
    </svg>
  );

  // Avatar layer — portaled onto <body>, above everything. The inner "vlr-spin" slowly rotates
  // around the sequence centre (matching the beams); each avatar's content counter-rotates upright.
  const avatarLayer = (
    <div className="vlr-avatars-layer">
      {/* No spin on mobile — rotating a full-viewport layer of glowing avatars costs a repaint per
          frame for the whole ring, and the beams underneath are static there anyway. */}
      <div className={"vlr-ring" + (heavyFx ? " vlr-spin" : "")} style={{ transformOrigin: `${cx}px ${cy}px` }}>
      {players.map((p, i) => {
        const a = angleOf(i);
        const ax = cx + rx * Math.cos(a);
        const ay = cy + ry * Math.sin(a);
        const ex = cx + exitRx * Math.cos(a);
        const ey = cy + exitRy * Math.sin(a);
        const atRing = entered && active;
        return (
          <div
            key={p.id}
            className="vlr-avatar"
            style={{
              // Anchored at the ring position; the fly in/out is a transform offset so the
              // animation stays on the compositor instead of re-laying-out every frame.
              left: ax,
              top: ay,
              opacity: active ? 1 : 0,
              transform: `translate(${atRing ? 0 : ex - ax}px, ${atRing ? 0 : ey - ay}px) translate(-50%, -50%) scale(${atRing ? 1 : 0.6})`,
              ["--pc" as any]: p.color,
            }}
          >
            <div className={heavyFx ? "vlr-avatar-inner vlr-avatar-counter" : "vlr-avatar-inner"}>
              <div className="vlr-avatar-ring" style={{ width: avatarPx + 10, height: avatarPx + 10 }}>
                <PlayerAvatar color={p.color} size={avatarPx} />
              </div>
              <span className="vlr-avatar-name">{p.name}</span>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );

  return (
    <>
      {beams}
      {ReactDOM.createPortal(avatarLayer, document.body)}
    </>
  );
}
